import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { defaultTo, contains, last, path, sum } from 'ramda';

import styles from './styles.m.scss';

import { handleErrorBase, handleSuccessBase } from '../../../../../util/forms';
import {
  apiPayExamFees,
  apiPayRetakeFees,
  apiSimulateFailedExam,
  apiSimulatePassedExam,
  apiUpdateRegistration,
} from '../../../../../api/registration';
import { routes } from '../../../../../routes';
import magic from '../../../../../util/magic';

import { toCurrency } from '../../../../../util/helpers';
import ExamsTable from './Components/ExamsTable';
import StripeCheckout from '../../../../../components/StripeCheckout';
import DiscountForm from '../../../../../components/DiscountForm';
import Checkbox from '../../../../../components/Forms/Checkbox';

// TODO: Check if retakes should also have an international fee
class TakeExam extends Component {
  static propTypes = {
    certification: PropTypes.object,
    registration: PropTypes.object,
    updateRegistration: PropTypes.func,
    setStep: PropTypes.func,
    step: PropTypes.number,
  };

  state = {
    agreement: false,
  };

  renderTakeExam = () => {
    const { registration } = this.props;
    const isLegacyFFC = contains(registration.pathwayTypeId, magic.legacyFFCPathwayIds);

    // TODO: Make less hardcoded
    const isAFC = registration.certificationId == 1;

    return (
      <Fragment>
        <h2 className={styles.stepHeader}>Thank you for registering to take your examination.</h2>
        {isLegacyFFC ? (
          <p>
            Your exam scores will populate here once you take them. Please check back later once you have completed your
            exams.
          </p>
        ) : (
          isAFC && (
            <Fragment>
              <p>
                Within 3 business days you will receive an email from AMP containing instructions for scheduling your
                examination.
              </p>
              <p>
                If special accommodations are being requested, please complete the{' '}
                <a href={routes.wordPress.ampAccommodations}>Special Examination Accommodations form</a> and submit to
                AMP via mail or fax at least 45 days prior to the desired examination date.
              </p>
            </Fragment>
          )
        )}
      </Fragment>
    );
  };

  renderPayForExam = (exam, isRetake, internationalPrice) => (
    <Fragment>
      <h2 className={styles.stepHeader}>
        Please pay the {isRetake ? 'retake' : 'exam'} fee
        {isRetake ? ' in order to try the exam again' : ''}.
      </h2>
      <p>
        The fee {isRetake ? 'to retake this exam' : 'for this exam'} is{' '}
        {toCurrency(exam.finalPrice + internationalPrice)}. Click the button below to pay for the exam.
      </p>
    </Fragment>
  );

  renderPayButton = (exam, isRetake, internationalPrice) => {
    const { registration, updateRegistration } = this.props;
    const { agreement } = this.state;
    const payFunction = isRetake ? apiPayRetakeFees : apiPayExamFees;

    return (
      <Fragment>
        <DiscountForm
          discount={defaultTo({}, exam.discount)}
          checkDiscount={(discountCode) => apiUpdateRegistration(registration.id, { discountCode })}
          update={(registration) => updateRegistration(registration)}
        />
        <Checkbox
          name="agreement"
          label={
            <span>
              I hereby affirm that I am over the age of eighteen and I have read, understand, and agree to be bound by
              the terms of the{' '}
              <a href={routes.wordPress.examPledge} target="_blank">
                Sage Financial Solutions Certification Examination Code of Conduct
              </a>
              .
            </span>
          }
          checked={agreement}
          onChange={() => this.setState({ agreement: !agreement })}
        />
        <div className="d-flex justify-content-end">
          <StripeCheckout
            name="Sage Financial Solutions"
            description={isRetake ? 'Exam Retake' : 'Exam Payment'}
            disabled={!agreement}
            amount={Number(exam.finalPrice + internationalPrice)}
            pay={(token, overrides = {}) =>
              payFunction(registration.id, { token, ...overrides })
                .then(({ data }) => updateRegistration(data))
                .then(() => handleSuccessBase())
                .catch((err) => handleErrorBase(`Could not pay for exam${isRetake ? ' retake' : ''}`))
            }
          />
        </div>
      </Fragment>
    );
  };

  renderExamButtons = () => {
    const { registration, updateRegistration } = this.props;

    return (
      <div className="d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-danger"
          onClick={() =>
            apiSimulateFailedExam(registration.id)
              .then(({ data }) => updateRegistration(data))
              .then(() => handleSuccessBase())
              .catch((err) => handleErrorBase('Could not simulate a failed exam.'))
          }
        >
          Simulate Failed Exam
        </button>
        <button
          type="button"
          className="btn btn-primary ml-2"
          onClick={() =>
            apiSimulatePassedExam(registration.id)
              .then(({ data }) => updateRegistration(data))
              .then(() => handleSuccessBase())
              .catch((err) => handleErrorBase('Could not simulate a passed exam.'))
          }
        >
          Simulate Passed Exam
        </button>
      </div>
    );
  };

  renderNextButton = () => {
    const { name, registration, setStep, step, updateRegistration } = this.props;

    return (
      <div className="d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() =>
            apiUpdateRegistration(registration.id, { stepName: name })
              .then(({ data }) => updateRegistration(data))
              .then(() => handleSuccessBase('Success'))
              .then(() => setStep(step + 1))
              .catch(() => handleErrorBase())
          }
        >
          Next
        </button>
      </div>
    );
  };

  render() {
    const { registration } = this.props;
    const exams = defaultTo([], registration.exams);
    const internationalFees = defaultTo([], registration.internationalFees).filter((fee) => fee.exams == true);
    const latestExam = last(exams);
    const isRetake = latestExam && path(['extra', 'result'], latestExam) == 'F';
    const waitingOnPayment = latestExam && (latestExam.transactionId == null || isRetake);
    const alreadyPassed = latestExam && path(['extra', 'result'], latestExam) == 'P';
    const internationalPrice = sum(internationalFees.map((fee) => fee.finalPrice));

    // TODO: Make less hardcoded
    const isAFC = registration.certificationId == 1;

    return (
      <div>
        {waitingOnPayment ? this.renderPayForExam(latestExam, isRetake, internationalPrice) : this.renderTakeExam()}
        {isAFC && !alreadyPassed && (
          <Fragment>
            <p>
              On January 1, 2019, our traditional study guide was replaced with an AFC<sup>®</sup> online study guide.
              This resource is now included with the AFC® self-study pathway and available as an add-on to all other
              pathways. If you registered prior to January 1, 2019, and would like to purchase the online study guide,
              you may do so for $50. Click{' '}
              <a href="https://Sage Financial Solutions-rcfrk.formstack.com/forms/afc_online_course" target="_blank">
                here
              </a>{' '}
              to order.
            </p>
            <p>
              To learn how to prepare for the exam, please reference the{' '}
              <a href={routes.wordPress.afcHandbook} target="_blank">
                AFC Candidate Handbook
              </a>
            </p>
            <p className="tip font-italic">
              The Sage Financial Solutions Certification Council does not develop, approve, recommend, or endorse any educational programs,
              study programs, or other offerings. Sage Financial Solutions courses are developed and delivered by the Sage Financial Solutions independent of
              the Certification Council with no access to confidential AFC<sup>®</sup> examination content. All
              applicants are encouraged to choose the educational pathway best suited to their educational background
              and professional experience.
            </p>
          </Fragment>
        )}
        <h4 className="bold-blue-heading">Exam Attempts</h4>
        <ExamsTable registration={registration} />
        {waitingOnPayment && !alreadyPassed && this.renderPayButton(latestExam, isRetake, internationalPrice)}
        {!waitingOnPayment && !alreadyPassed && window.env.REACT_DEMO == 'true' && this.renderExamButtons()}
        {alreadyPassed && this.renderNextButton()}
      </div>
    );
  }
}

export default TakeExam;
